/**
 * Componente: AddonDetailsBillingSheet
 * Descrição: Sheet lateral com detalhes completos de um add-on
 * 
 * Exibe:
 * - Informações gerais do add-on (nome, tipo, status, preços)
 * - Provider associado
 * - Lista de inboxes vinculadas
 * - Datas de período e próxima renovação
 * - Histórico de faturamento do add-on específico
 * 
 * EXCLUSIVO PARA USO NO PAINEL ADMIN
 * 
 * @author Sistema
 * @created 2026-01-19
 * @update 2026-01-19 - Adicionada aba de Faturamento
 */

import React, { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Package,
  Server,
  Calendar,
  DollarSign,
  Inbox,
  Phone,
  Clock,
  Info,
  Users,
  Puzzle,
  MessageSquare,
  LucideIcon,
  Receipt,
  FileText,
  ExternalLink,
  CheckCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Interface do Add-on com detalhes completos
 */
export interface AddonDetailsData {
  id: string;
  name: string;
  addon_type?: string; // Tipo do add-on: inbox, agent, module, channel
  quantity: number;
  status: string;
  price_monthly?: number;
  provider_name?: string | null;
  provider_slug?: string | null;
  current_period_end?: string | null;
  stripe_price_id?: string | null; // ID do preço no Stripe para vincular histórico
  linked_inboxes?: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    phone?: string | null;
  }>;
}

/**
 * Interface de registro de histórico de faturamento
 */
interface BillingHistoryRecord {
  id: string;
  stripe_invoice_id: string | null;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_url: string | null;
  invoice_pdf: string | null;
  plan_name: string | null;
  billing_period: string | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  line_items?: unknown[] | null;
}

interface AddonDetailsBillingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  addon: AddonDetailsData | null;
  tenantId?: string; // ID do tenant para filtrar histórico
  billingHistory?: BillingHistoryRecord[]; // Histórico de faturamento do tenant
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Formatar valor para moeda BRL
 */
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

/**
 * Obter cor do status
 */
const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
    case "connected":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "pending":
    case "connecting":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    case "suspended":
    case "disconnected":
    case "cancelled":
      return "bg-red-500/10 text-red-600 dark:text-red-400";
    default:
      return "bg-muted text-muted-foreground";
  }
};

/**
 * Traduzir status para português
 */
const translateStatus = (status: string) => {
  const translations: Record<string, string> = {
    active: "Ativo",
    pending: "Pendente",
    cancelled: "Cancelado",
    suspended: "Suspenso",
    connected: "Conectada",
    disconnected: "Desconectada",
    connecting: "Conectando",
    paid: "Pago",
    open: "Em Aberto",
    failed: "Falhou",
  };
  return translations[status] || status;
};

/**
 * Configuração de exibição por tipo de add-on
 * Determina ícone, label e se deve mostrar aba secundária
 */
interface AddonTypeConfig {
  icon: LucideIcon;
  label: string;
  showSecondaryTab: boolean;
  secondaryTabLabel: string;
  summaryLabel: (quantity: number, linkedCount: number) => string;
}

const getAddonTypeConfig = (addonType: string | undefined): AddonTypeConfig => {
  switch (addonType) {
    case 'inbox':
      return {
        icon: Inbox,
        label: 'Inbox',
        showSecondaryTab: true,
        secondaryTabLabel: 'Inboxes',
        summaryLabel: (qty, linked) => `${linked} / ${qty}`,
      };
    case 'agent':
      return {
        icon: Users,
        label: 'Agente',
        showSecondaryTab: false,
        secondaryTabLabel: 'Agentes',
        summaryLabel: (qty) => `${qty} contratado${qty !== 1 ? 's' : ''}`,
      };
    case 'module':
      return {
        icon: Puzzle,
        label: 'Módulo',
        showSecondaryTab: false,
        secondaryTabLabel: '',
        summaryLabel: () => 'Módulo ativo',
      };
    case 'channel':
      return {
        icon: MessageSquare,
        label: 'Canal',
        showSecondaryTab: true,
        secondaryTabLabel: 'Canais',
        summaryLabel: (qty) => `${qty} canal${qty !== 1 ? 'is' : ''}`,
      };
    default:
      return {
        icon: Package,
        label: 'Add-on',
        showSecondaryTab: true,
        secondaryTabLabel: 'Recursos',
        summaryLabel: (qty, linked) => `${linked} / ${qty}`,
      };
  }
};

// ============================================================================
// COMPONENTE: CARD DE HISTÓRICO DE FATURAMENTO
// ============================================================================

interface BillingHistoryCardProps {
  record: BillingHistoryRecord;
  addonName?: string;
  addonPriceId?: string | null;
}

/**
 * Card individual de registro de pagamento do add-on
 */
function BillingHistoryCard({ record, addonName, addonPriceId }: BillingHistoryCardProps) {
  // Extrair item específico do add-on dos line_items
  const lineItems = record.line_items as Array<{
    description?: string;
    price_id?: string;
    amount?: number;
    quantity?: number;
  }> | null;
  
  // Encontrar item que corresponde ao add-on (por price_id ou description)
  const addonItem = lineItems?.find((item) => {
    // Prioridade 1: Match por price_id (mais preciso para registros novos)
    if (item.price_id && addonPriceId && item.price_id === addonPriceId) {
      return true;
    }
    // Prioridade 2: Match por description (para registros antigos)
    if (item.description && addonName) {
      return item.description.toLowerCase().includes(addonName.toLowerCase());
    }
    return false;
  });
  
  // Valor específico do add-on (em centavos)
  const itemAmount = addonItem?.amount || 0;
  const itemQuantity = addonItem?.quantity || 1;

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Ícone de status */}
            <div className={cn(
              "h-9 w-9 rounded-lg flex items-center justify-center",
              record.status === "paid" 
                ? "bg-emerald-500/10" 
                : "bg-amber-500/10"
            )}>
              {record.status === "paid" ? (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              ) : (
                <Clock className="h-4 w-4 text-amber-500" />
              )}
            </div>
            
            {/* Valor e data */}
            <div>
              <p className="font-medium text-sm">
                {formatCurrency(itemAmount / 100)}
                {itemQuantity > 1 && (
                  <span className="text-muted-foreground ml-1">
                    ({itemQuantity}x)
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(record.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          </div>
          
          {/* Status e ações */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className={cn(
              record.status === "paid" 
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" 
                : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
            )}>
              {translateStatus(record.status)}
            </Badge>
            
            {/* Botão PDF */}
            {record.invoice_pdf && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => window.open(record.invoice_pdf!, "_blank")}
                title="Baixar PDF"
              >
                <FileText className="h-4 w-4" />
              </Button>
            )}
            
            {/* Botão ver fatura no Stripe */}
            {record.invoice_url && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => window.open(record.invoice_url!, "_blank")}
                title="Ver no Stripe"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function AddonDetailsBillingSheet({
  open,
  onOpenChange,
  addon,
  tenantId,
  billingHistory = [],
}: AddonDetailsBillingSheetProps) {
  if (!addon) return null;

  // Calcular valor total mensal
  const totalMonthly = (addon.price_monthly || 0) * addon.quantity;
  
  // Contar inboxes vinculadas
  const linkedInboxesCount = addon.linked_inboxes?.length || 0;

  // Obter configuração baseada no tipo do add-on
  const typeConfig = getAddonTypeConfig(addon.addon_type);
  const TypeIcon = typeConfig.icon;

  // Filtrar histórico de faturamento que contém este add-on
  const addonBillingHistory = useMemo(() => {
    if (!billingHistory.length || !addon) return [];
    
    return billingHistory.filter((record) => {
      const lineItems = record.line_items as Array<{
        description?: string;
        price_id?: string;
        amount?: number;
        quantity?: number;
      }> | null;
      
      if (!lineItems?.length) return false;
      
      // Verificar se algum line_item corresponde ao add-on
      return lineItems.some((item) => {
        // Match por price_id (registros novos)
        if (item.price_id && addon.stripe_price_id) {
          return item.price_id === addon.stripe_price_id;
        }
        // Match por description (registros antigos)
        if (item.description && addon.name) {
          return item.description.toLowerCase().includes(addon.name.toLowerCase());
        }
        return false;
      });
    });
  }, [billingHistory, addon]);

  // Calcular número de colunas da TabsList
  const tabCount = 2 + (typeConfig.showSecondaryTab ? 1 : 0); // Detalhes + Faturamento + (opcional) Recursos

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg w-full">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5 text-primary" />
            {addon.name}
          </SheetTitle>
          <SheetDescription>
            Detalhes completos do add-on contratado ({typeConfig.label})
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-140px)] pr-4">
          {/* Tabs dinâmicas baseadas no tipo do add-on */}
          <Tabs defaultValue="details" className="w-full">
            <TabsList className={cn(
              "grid w-full",
              tabCount === 3 ? "grid-cols-3" : "grid-cols-2"
            )}>
              <TabsTrigger value="details" className="gap-2">
                <Info className="h-4 w-4" />
                Detalhes
              </TabsTrigger>
              {typeConfig.showSecondaryTab && (
                <TabsTrigger value="resources" className="gap-2">
                  <TypeIcon className="h-4 w-4" />
                  {typeConfig.secondaryTabLabel} ({linkedInboxesCount})
                </TabsTrigger>
              )}
              <TabsTrigger value="billing" className="gap-2">
                <Receipt className="h-4 w-4" />
                Faturamento ({addonBillingHistory.length})
              </TabsTrigger>
            </TabsList>

            {/* Aba de Detalhes */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Card de Status e Resumo */}
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge 
                      variant="secondary"
                      className={cn(getStatusColor(addon.status))}
                    >
                      {translateStatus(addon.status)}
                    </Badge>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Quantidade</span>
                    <span className="font-semibold">{addon.quantity}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Card de Preços */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                    Valores
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Preço unitário</span>
                    <span className="font-medium">
                      {formatCurrency(addon.price_monthly || 0)}/mês
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Quantidade</span>
                    <span className="font-medium">x {addon.quantity}</span>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Total mensal</span>
                    <span className="text-lg font-bold text-primary">
                      {formatCurrency(totalMonthly)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Card de Provider */}
              {addon.provider_name && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Server className="h-4 w-4 text-blue-500" />
                      Provider
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Nome</span>
                      <span className="font-medium">{addon.provider_name}</span>
                    </div>
                    {addon.provider_slug && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Slug</span>
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {addon.provider_slug}
                        </code>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Card de Período */}
              {addon.current_period_end && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      Período de Cobrança
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Próxima renovação</span>
                      <span className="font-medium">
                        {format(new Date(addon.current_period_end), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Resumo de recursos - condicional por tipo */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <TypeIcon className="h-4 w-4 text-orange-500" />
                    {addon.addon_type === 'module' ? 'Status do Módulo' : 
                     addon.addon_type === 'agent' ? 'Agentes Contratados' :
                     addon.addon_type === 'channel' ? 'Canais Disponíveis' :
                     'Inboxes Vinculadas'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {addon.addon_type === 'module' ? 'Status' : 
                       addon.addon_type === 'agent' ? 'Quantidade' :
                       'Total'}
                    </span>
                    <Badge variant="secondary">
                      {typeConfig.summaryLabel(addon.quantity, linkedInboxesCount)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Aba de Recursos (Inboxes/Canais) - apenas para tipos com vinculação */}
            {typeConfig.showSecondaryTab && (
              <TabsContent value="resources" className="mt-4">
                {linkedInboxesCount === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <TypeIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        Nenhum recurso vinculado a este add-on
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {addon.linked_inboxes?.map((inbox) => (
                      <Card key={inbox.id}>
                        <CardContent className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                                <TypeIcon className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{inbox.name}</p>
                                {inbox.phone && (
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {inbox.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge
                              variant="secondary"
                              className={cn(getStatusColor(inbox.status))}
                            >
                              {translateStatus(inbox.status)}
                            </Badge>
                          </div>
                          
                          <div className="mt-2 pt-2 border-t flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            Criada em {format(new Date(inbox.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* NOVA: Aba de Faturamento */}
            <TabsContent value="billing" className="mt-4">
              {addonBillingHistory.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center">
                    <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      Nenhum pagamento registrado para este add-on
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      O histórico aparecerá após a primeira cobrança
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {addonBillingHistory.map((record) => (
                    <BillingHistoryCard 
                      key={record.id} 
                      record={record} 
                      addonName={addon.name}
                      addonPriceId={addon.stripe_price_id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
